
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Segment } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Building2, Users, PieChart, Info, ChevronRight, AlertCircle } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { buildTechStackCategories, applyRules } from '@/lib/segmentApplyRules';
import AccountFitBadge from '@/components/accounts/AccountFitBadge';
import { enrolmentColumnHeader, formatEnrolmentCell } from '@/lib/enrolmentDisplay';

export default function SegmentResults({ currentTenant: currentTenantProp }) {
    const [segment, setSegment] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();
    const contextUser = useUser();
    const contextTenant = useCurrentTenant();
    const currentTenant = currentTenantProp || contextTenant;
    const segmentId = useMemo(() => new URLSearchParams(location.search).get('id'), [location.search]);
    /** Optional; BE GET-by-id ignores ?tenant_id= and uses JWT tenant. Kept for deep links only. */
    const urlTenantId = useMemo(() => new URLSearchParams(location.search).get('tenant_id'), [location.search]);

    useEffect(() => {
        if (!segmentId) {
            setError("No segment ID provided");
           setIsLoading(false);
           return;
       }
       if (!contextUser) return;
       const fetchData = async () => {
        setIsLoading(true);
        try {
            const user = contextUser;
            
            // Tenant query is ignored by BE for segments — JWT + cookie session determine access.
            const segmentData = await Segment.get(segmentId, urlTenantId || undefined);
                
            console.log('📂 RESULTS: Loaded segment:', {
                id: segmentData.id,
                name: segmentData.name,
                tenant_id: segmentData.tenant_id,
                user_id: segmentData.user_id,
                rules: segmentData.rules
            });
            
            // CHECK USER OWNERSHIP - only allow viewing own segments
            if (segmentData.user_id && segmentData.user_id !== user?.id) {
                setError("You don't have permission to view this segment.");
                setIsLoading(false);
                return;
            }
                
                const effectiveTenantId =
                    segmentData.tenant_id ||
                    urlTenantId ||
                    resolveEffectiveTenant(
                        user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
                        currentTenant,
                        user,
                        'instructure'
                    );
                
                console.log('🏢 RESULTS: Tenant context:', {
                    segmentTenant: segmentData.tenant_id,
                    effectiveTenantId
                });
                
                let tenantFilter;
if (effectiveTenantId === 'instructure') {
    tenantFilter = { $in: [effectiveTenantId, 'clairelabs', 'default', 'global'] };
} else if (effectiveTenantId === 'smowl') {
    tenantFilter = { $in: [effectiveTenantId] };
} else if (effectiveTenantId === 'diambra') {
    tenantFilter = { $in: [effectiveTenantId, 'smowl', 'instructure', 'clairelabs', 'base44', 'default', 'global'] };
} else if (effectiveTenantId === 'clairelabs') {
    tenantFilter = { $in: [effectiveTenantId, 'default', 'global'] };
} else if (effectiveTenantId === 'edqwest' || effectiveTenantId === 'faria') {
    tenantFilter = { $in: ['edqwest', 'faria', 'global'] };
} else if (effectiveTenantId === 'wayground') {
    tenantFilter = { $in: [effectiveTenantId, 'plataformaa', 'global'] };
} else {
    // Default for: calmedica, kairadigital, demarque, prowise, skillwell, sebit, plataformaa, etc.
    tenantFilter = { $in: [effectiveTenantId, 'global'] };
}
                
                const allAccountsRaw = await AccountFinal.filter({ tenant_id: tenantFilter });
                
                const accountsWithOverrides = allAccountsRaw
                    .map(acc => applyTenantOverrides(acc, effectiveTenantId))
                    .filter(acc => acc.is_visible !== false);
                
                const allowedCountries = {
                    instructure: ['Australia', 'United Kingdom', 'Hong Kong'],
                    clairelabs: ['Australia', 'Hong Kong'],
                    smowl: ['Hong Kong', 'South Africa', 'Australia', 'United Kingdom', 'Kenya'],
                    diambra: ['United Kingdom'],
                    edqwest: ['France', 'UAE', 'United Kingdom'],
                    faria: ['France', 'United Arab Emirates', 'Spain', 'Italy', 'United Kingdom', 'Luxembourg'],
                    calmedica: ['France'],
                    kairadigital: ['France', 'United Kingdom', 'Sweden', 'Switzerland', 'Germany', 'Netherlands'],
                    demarque: ['Canada', 'Morocco', 'Germany', 'Tunisia', 'USA', 'Kenya', 'South Africa'],
                    demarque_V2: ['Canada', 'Morocco', 'Germany', 'Tunisia', 'USA', 'Kenya', 'South Africa'],
                    demarque_v2: ['Canada', 'Morocco', 'Germany', 'Tunisia', 'USA', 'Kenya', 'South Africa'],
                    prowise: ['Belgium', 'Spain', 'Netherlands', 'Poland'],
                    skillwell: ['USA'],
                    sebit: ['France', 'United Kingdom', 'Switzerland', 'Germany', 'Netherlands', 'United Arab Emirates'],
                    plataformaa: ['Chile', 'Colombia', 'Mexico', 'Peru'],
                    wayground: ['Chile', 'Colombia', 'Mexico', 'Peru', 'Argentina', 'Uruguay'],
                };
                
                const hongKongAccountsToExclude = [
                    'City University of Hong Kong (CityU)',
                    'The Hong Kong University of Science and Technology (HKUST)'
                ];
                
                const filteredAccounts = accountsWithOverrides.filter(acc => {
                    if (effectiveTenantId === 'instructure' && hongKongAccountsToExclude.includes(acc.account_name)) {
                        return false;
                    }
                    
                    const allowed = allowedCountries[effectiveTenantId];
                    if (allowed && acc.country) {
                        return allowed.includes(acc.country);
                    }
                    
                    return true;
                });
                
                const parsedRules = (segmentData.rules || []).map(rule => ({
                    ...rule,
                    value: (rule.value && typeof rule.value === 'string' && rule.value.startsWith('[')) ? JSON.parse(rule.value) : rule.value
                }));
                
                console.log('📋 RESULTS: Parsed rules:', parsedRules);
                
                const techStackCategories = buildTechStackCategories(filteredAccounts);
                const matchingAccounts = applyRules(filteredAccounts, parsedRules, techStackCategories);
                
                console.log('📊 RESULTS loaded accounts:', {
                    tenant: effectiveTenantId,
                    allowedCountries: allowedCountries[effectiveTenantId],
                    total: allAccountsRaw.length,
                    afterOverrides: accountsWithOverrides.length,
                    afterFiltering: filteredAccounts.length,
                    matching: matchingAccounts.length,
                    countries: [...new Set(filteredAccounts.map(a => a.country))],
                    accountIds: filteredAccounts.map(a => a.id),
                    parsedRules: parsedRules
                });
                
                setAccounts(matchingAccounts);
                setSegment(segmentData);
            } catch (err) {
                console.error('❌ RESULTS error:', err);
                setError("Failed to load segment. It may have been deleted.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [segmentId, urlTenantId, contextUser, currentTenant]);

    const countryDistribution = useMemo(() => {
        const dist = accounts.reduce((acc, account) => {
            acc[account.country] = (acc[account.country] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(dist).sort((a,b) => b[1] - a[1]);
    }, [accounts]);

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
                <Skeleton className="h-8 w-64" />
                <div className="grid md:grid-cols-3 gap-6"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
                <Skeleton className="h-96" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-20">
                <AlertCircle className="w-12 h-12 mx-auto text-red-300" />
                <h2 className="mt-4 text-xl font-semibold">{error}</h2>
                <Link to={createPageUrl('Segments')}><Button variant="outline" className="mt-4">Back to Segments</Button></Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Link to={createPageUrl('Segments')} className="flex items-center gap-2 text-sm text-[#5C6B7A] hover:text-[#0A1A2F] mb-4">
                <ArrowLeft className="w-4 h-4" /> Back to Segments
            </Link>
            <h1 className="text-3xl font-bold text-[#0A1A2F] mb-2">{segment.name}</h1>
            <p className="text-lg text-[#5C6B7A] mb-6">A detailed view of the accounts matching your segment criteria.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="border-0 shadow-md"><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Total Accounts</CardTitle><Users className="w-4 h-4 text-[#5C6B7A]" /></CardHeader><CardContent><div className="text-2xl font-bold">{accounts.length}</div><p className="text-xs text-[#5C6B7A]">accounts match</p></CardContent></Card>
                <Card className="border-0 shadow-md"><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Country Distribution</CardTitle><PieChart className="w-4 h-4 text-[#5C6B7A]" /></CardHeader><CardContent>{countryDistribution.length > 0 ? (<div className="space-y-1">{countryDistribution.slice(0, 2).map(([c, n]) => <div key={c} className="text-sm flex justify-between"><span>{c}</span><span className="font-semibold">{n}</span></div>)}{countryDistribution.length > 2 && <p className="text-xs text-[#5C6B7A]">+ {countryDistribution.length - 2} more</p>}</div>) : <p className="text-sm text-[#5C6B7A]">N/A</p>}</CardContent></Card>
                <Card className="border-0 shadow-md"><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Applied Rules</CardTitle><Info className="w-4 h-4 text-[#5C6B7A]" /></CardHeader><CardContent><div className="text-2xl font-bold">{segment.rules.length}</div><p className="text-xs text-[#5C6B7A]">rules applied</p></CardContent></Card>
            </div>
            
             <Card className="border-0 shadow-md">
                <CardHeader><CardTitle>Matching Accounts</CardTitle></CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader><TableRow><TableHead>Account</TableHead><TableHead>Country</TableHead><TableHead>Account Fit Score</TableHead><TableHead>{enrolmentColumnHeader(accounts)}</TableHead><TableHead></TableHead></TableRow></TableHeader>
                        <TableBody>
                            {accounts.map(account => {
                                return (
                                <TableRow key={account.id}>
                                    <TableCell>
                                        <Link to={createPageUrl(`AccountFinal?id=${account.id}`)} className="flex items-center gap-3 hover:text-[#00C2C2]">
                                            {account.logo_url ? <img src={account.logo_url} alt={account.account_name} className="w-8 h-8 object-contain rounded-md border p-0.5" /> : <div className="w-8 h-8 rounded-md bg-gray-200 flex items-center justify-center"><Building2 className="w-4 h-4 text-[#5C6B7A]" /></div>}
                                            <span className="font-semibold">{account.account_name}</span>
                                        </Link>
                                    </TableCell>
                                    <TableCell>{account.country}</TableCell>
                                    <TableCell><AccountFitBadge account={account} /></TableCell>
                                    <TableCell>{formatEnrolmentCell(account.enrolment, account.enrolment_measure)}</TableCell>
                                    <TableCell><Link to={createPageUrl(`AccountFinal?id=${account.id}`)}><ChevronRight className="w-5 h-5 text-gray-400" /></Link></TableCell>
                                </TableRow>
                            )})}
                        </TableBody>
                    </Table>
                     {accounts.length === 0 && <div className="text-center py-10 text-sm text-[#5C6B7A]"><p>No accounts matched the criteria.</p></div>}
                </CardContent>
             </Card>
        </div>
    );
}
